<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Stores
	import RaceStore from '@/Stores/Racing/RaceStore.js';
	// Constants
	import { EventStatus } from '@/Constants/Event';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Switch from '@/Components/Switch.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Services
	import { notify } from '@/Services/Notify';

	dayjs.extend(localizedFormat);

	export let open = false;
	export let meeting = undefined;

	let loading = false;
	let abandonRaceList = [];

	function toggleAbandonRace(race) {
		if (abandonRaceList.includes(race.id)) {
			removeRaceFromList(race);
			return;
		}
		addRaceToList(race);
	}

	function addRaceToList(race) {
		abandonRaceList = [race.id, ...abandonRaceList];
	}

	function removeRaceFromList(race) {
		abandonRaceList = abandonRaceList.filter(function (value) {
			return value !== race.id;
		});
	}

	function toggleAbandonAllRace(checked) {
		if (checked) {
			allAddRacesToList();
			return;
		}
		removeAllRacesFromList();
	}

	function allAddRacesToList() {
		abandonRaceList = [];
		$RaceStore
			.where('meeting_id', meeting.id)
			.sortBy('race_number')
			.each((race) => {
				abandonRaceList = [race.id, ...abandonRaceList];
			});
	}

	function removeAllRacesFromList() {
		abandonRaceList = [];
	}

	function abandonRaces() {
		loading = true;
		$RaceStore
			.where('meeting_id', meeting.id)
			.sortBy('race_number')
			.each((race) => {
				if (abandonRaceList.includes(race.id) && ![EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)) {
					race
						.abandon()
						.then(() => {
							open = false;
							notify({
								type: 'success',
								title: `R${race.race_number} @ ${meeting.name}`,
								message: 'Race has been successfully abandoned',
								timeout: 3
							});
						})
						.catch(() => {
							notify({
								type: 'danger',
								title: `R${race.race_number} @ ${meeting.name}`,
								message: 'Failed to abandon race',
								timeout: 3
							});
						});
				}
			});
	}
</script>

{#if open && meeting}
	<Modal on:close={() => (open = false)} className="mx-2 md:w-1/2 md:mx-auto">
		<!-- TITLE -->
		<div class="pt-2 font-semibold text-xl text-center uppercase mb-4" slot="header">
			Abandon race/s at {meeting.name}
		</div>

		<!-- HEADER -->
		<div class="text-red-600 text-center font-semibold text-lg">
			<p>WARNING: Abandoning a race or races will result in the following</p>
		</div>
		<div class="text-red-600 text-left mx-16 text-lg">
			<ol>
				<li>
					* Any associated race currently being paid on interim or final results will be unfinalised and bets cancelled
					and refunded.
				</li>
				<li>
					* Any associated race that has not commenced, all Singles, Quinellas, Exactas, Trifectas, & First Fours will
					be cancelled and refunded.
				</li>
			</ol>
		</div>

		<!-- MEETING -->
		<div class="table w-full px-4 mt-2 mb-4 text-center table-auto">
			<div class="table-row-group">
				<div class="table-row font-bold uppercase">
					<div class="table-cell px-1 py-2">
						<FontIcon icon={meeting.race_type.name.toLowerCase()} />
						{meeting.name}
						<span class="font-medium"> - {dayjs(meeting.start_time).format('ll')}</span>
					</div>
					<div class="table-cell px-1 text-right pr-16">
						Select All
						{#key abandonRaceList}
							<Switch
								on:change={(ev) => {
									toggleAbandonAllRace(ev.target.checked);
								}}
								disabled={loading}
								checked={abandonRaceList.length === $RaceStore.where('meeting_id', meeting.id).count()}
								className="-mt-1"
								small={true}
							/>
						{/key}
					</div>
				</div>
			</div>
		</div>

		<!-- RACES -->
		<div class="table w-full px-4 mt-2 mb-4 text-center table-auto">
			<div class="table-row-group">
				<div class="table-row font-bold uppercase">
					<div class="table-cell px-1 py-2">Race</div>
					<div class="table-cell px-1 text-left">Name</div>
					<div class="table-cell px-1">Start Time</div>
					<div class="table-cell px-1">Abandon</div>
				</div>
			</div>
			<div class="table-row-group">
				{#each $RaceStore.where('meeting_id', meeting.id).sortBy('race_number').all() as race (race.id)}
					<div class="table-row hover:bg-gray-200">
						<div
							class="table-cell py-2 border-b border-row-separator"
							class:opacity-50={[EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)}
						>
							{race.race_number}
						</div>
						<div
							class="table-cell text-left border-b border-row-separator"
							class:opacity-50={[EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)}
						>
							{race.name}
						</div>
						<div
							class="table-cell border-b border-row-separator"
							class:opacity-50={[EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)}
						>
							{dayjs(race.start_time).format('LT')}
						</div>
						<div
							class="table-cell border-b border-row-separator"
							class:opacity-50={[EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)}
						>
							{#key abandonRaceList}
								<Switch
									on:change={() => toggleAbandonRace(race)}
									disabled={loading || [EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)}
									name={'abandonRace' + race.race_number}
									checked={abandonRaceList.includes(race.id) &&
										![EventStatus.Abandoned, EventStatus.Paid].includes(race.status.id)}
									small={true}
								/>
							{/key}
						</div>
					</div>
				{/each}
			</div>
		</div>

		<!-- CONTROLS -->
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<LoadingButton {loading} className="mb-4 btn btn-primary" on:click={() => abandonRaces()}>
				Abandon Races
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}> Cancel </button>
		</div>
	</Modal>
{/if}
